/**
 * IST wall-clock helpers for the pre-open schedulers.
 *
 * The exchange day is defined in Asia/Kolkata and the server may run anywhere,
 * so "08:15" has to mean 08:15 in Mumbai regardless of the host's timezone or
 * of whether it was restarted across a DST boundary somewhere else. Derived
 * from `Intl` rather than by adding 5h30m to a UTC date: the offset arithmetic
 * works today only because India does not observe DST, which is a fact about
 * India rather than something this code should assume.
 *
 * Lives in `shared` because THREE things need it in two processes: the API's
 * catalogue sync and broker re-auth schedulers, and the engine's feed
 * watchdog, which must know whether a silent socket at 16:18 is broken or
 * simply after the close. A second copy in the engine would be a second
 * definition of when the market is open.
 *
 * Deliberately not a cron dependency — see `instrumentSync.scheduler.ts` for
 * why, and for where this moves when Phase 7/8 need durable scheduling.
 *
 * NOTE ON TRADING DAYS: `isWeekendIst` skips Saturday and Sunday only.
 * Exchange holidays are not modelled until Phase 8's `TradingCalendar`, so a
 * job will still run on Diwali. Nothing here is a session calendar.
 */
const WEEKDAY = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
const FORMAT = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false,
});
export function istParts(d = new Date()) {
    const parts = Object.fromEntries(FORMAT.formatToParts(d).map((p) => [p.type, p.value]));
    return {
        y: Number(parts.year),
        m: Number(parts.month),
        day: Number(parts.day),
        // en-GB gives "24" for midnight rather than "00".
        h: Number(parts.hour) % 24,
        min: Number(parts.minute),
        weekday: WEEKDAY[parts.weekday ?? 'Mon'] ?? 1,
    };
}
/**
 * The IST calendar day as `YYYY-MM-DD`.
 *
 * The trading-session key. Phase 4.7 puts it in `Fill`'s idempotency key
 * because XTS numbers orders per session and reuses execution ids on a later
 * day — so a fill is identified by its id AND the day it belongs to.
 *
 * Always derived from the FILL's own timestamp, never from `now`: a re-poll
 * after IST midnight must resolve to the same day, or the same fill is
 * written twice.
 */
export function istDateKey(d = new Date()) {
    const p = istParts(d);
    return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
export function isWeekendIst(d = new Date()) {
    const w = istParts(d).weekday;
    return w === 0 || w === 6;
}
/**
 * Milliseconds until the next occurrence of `hour:minute` IST.
 *
 * Always strictly in the future, and aligned to the top of the minute so a
 * scheduler that re-arms itself does not drift forwards a few milliseconds
 * every day.
 */
export function msUntilIst(hour, minute, now = new Date()) {
    const p = istParts(now);
    let deltaMinutes = hour * 60 + minute - (p.h * 60 + p.min);
    if (deltaMinutes <= 0)
        deltaMinutes += 24 * 60;
    return deltaMinutes * 60_000 - now.getSeconds() * 1000 - now.getMilliseconds();
}
/** "08:15 IST", for a log line. */
export const istLabel = (hour, minute) => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} IST`;
//# sourceMappingURL=istClock.js.map